/**
 * Configuración de Vuetify.
 *
 * Dos responsabilidades:
 *  1. El tema (solo oscuro). Además de los colores originales, expone la paleta
 *     real de marca, que hasta ahora vivía escrita a mano en 41 ficheros.
 *  2. Los `defaults`, que son la única forma de dejar de pelearse con Vuetify a
 *     base de !important: si el radio y la densidad correctos son el valor por
 *     defecto, las páginas no tienen que corregirlos.
 *
 * De momento los defaults tocan solo forma y densidad. `variant`, `flat` y el
 * cambio de `primary` se dejan para el final del rework, cuando ya no queden
 * estilos locales compensándolos (hay más de 100 usos de color="primary").
 *
 * Los valores en crudo están duplicados a propósito con assets/tokens.css: el
 * tema de Vuetify necesita colores literales, no var(--ws-*).
 */
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { VCalendar } from 'vuetify/labs/VCalendar'
import { VDateInput } from 'vuetify/labs/VDateInput'
import { en, es } from 'vuetify/locale'
import { mdiSet } from '@/plugins/icons'

// Colores: Shows #193cb8, Películas #861043, Series #6e11b0
const darkTheme = {
  dark: true,
  colors: {
    // La identidad de la app es el morado→rosa: `primary` era el azul heredado
    // y dejaba spinners, barras y botones sueltos de otro color.
    primary: '#8e2de2',
    secondary: '#8d99AE',
    accent: '#6e11b0',
    error: '#CF6679',
    info: '#193cb8',
    success: '#81C784',
    warning: '#861043',
    background: '#121212',
    surface: '#1E1E1E',

    // Marca (--ws-brand-* en tokens.css)
    brand: '#8e2de2',
    'brand-pink': '#ff007f',

    // Por tipo de contenido, para chips y badges
    'type-serie': '#ff6b9d',
    'type-pelicula': '#f6339a',
    'type-espectaculo': '#2b7fff',
    'type-articulo': '#10b981',

    // Superficies (--ws-surface-* en tokens.css)
    'surface-bright': '#26262a',
    'surface-variant': '#2d2d30',
  },
}

const fieldDefaults = {
  variant: 'outlined',
  density: 'comfortable',
  hideDetails: 'auto',
}

export const vuetify = createVuetify({
  components: {
    ...components,
    VCalendar,
    VDateInput,
  },
  directives,
  // Vuetify tiene su propio locale, y de él dependen los meses y los días del
  // calendario de VDateInput. Sin esto salía en inglés con la app en español.
  // Lo sincroniza `setVuetifyLocale` cuando cambia el idioma de la app.
  locale: {
    locale: 'es',
    fallback: 'en',
    messages: { es, en },
  },
  icons: {
    defaultSet: 'mdi',
    sets: { mdi: mdiSet },
  },
  theme: {
    defaultTheme: 'dark',
    themes: {
      dark: darkTheme,
    },
  },
  defaults: {
    // La escala de radios de Vuetify (sm 2px · 4px · lg 8px · xl 24px) no
    // coincide con la nuestra, así que la forma se aplica por clase desde
    // assets/components.css en vez de con la prop `rounded`.
    VBtn: { class: 'ws-btn' },
    VCard: { class: 'ws-card' },
    VChip: { rounded: 'pill' },
    VTextField: fieldDefaults,
    VTextarea: fieldDefaults,
    VSelect: fieldDefaults,
    VAutocomplete: fieldDefaults,
    VCombobox: fieldDefaults,
    VDateInput: { ...fieldDefaults, prependIcon: '', prependInnerIcon: 'mdi-calendar' },
    VDataTable: { density: 'comfortable', hover: true },
    VProgressCircular: { color: 'brand' },
    VTooltip: { location: 'top' },
  },
})

/** Alinea el locale de Vuetify con el de la app (calendario, paginación, etc.). */
export function setVuetifyLocale(locale) {
  if (locale === 'es' || locale === 'en') {
    vuetify.locale.current.value = locale
  }
}

export default vuetify
